import {
  resolveEffectiveScopeInterventionMode,
  scopeInterventionModesOverlap,
} from '@evimo/core/modules/partner-maintenance-rules/domain/partner-maintenance-scope-mode.utils';
import type {
  PartnerMaintenanceInterventionMode,
  PartnerMaintenanceScopeDimension,
  PartnerMaintenanceScopeEffect,
  PartnerMaintenanceScopeInterventionMode,
} from '@evimo/types/enums/partner-maintenance-rules';

export type ScopeFlatAtom = {
  ruleId: string;
  dimension: PartnerMaintenanceScopeDimension;
  effect: PartnerMaintenanceScopeEffect;
  value: string;
  interventionMode: PartnerMaintenanceScopeInterventionMode | null;
};

export type ScopeIncludeExcludeConflict = {
  dimension: PartnerMaintenanceScopeDimension;
  value: string;
  includeRuleId: string;
  excludeRuleId: string;
};

type ScopeRuleLike = {
  id?: string;
  dimension: string;
  effect: PartnerMaintenanceScopeEffect;
  value?: string | null;
  values?: string[];
  interventionMode?: PartnerMaintenanceScopeInterventionMode | null;
};

export function flattenScopeEffectAtoms(
  rules: ScopeRuleLike[],
): ScopeFlatAtom[] {
  const atoms: ScopeFlatAtom[] = [];
  for (const rule of rules) {
    const ruleId = rule.id ?? `${rule.dimension}:${rule.effect}`;
    const values =
      rule.values?.filter((v) => v.trim().length > 0) ??
      (rule.value?.trim() ? [rule.value.trim()] : []);
    for (const value of values) {
      atoms.push({
        ruleId,
        dimension: rule.dimension as PartnerMaintenanceScopeDimension,
        effect: rule.effect,
        value,
        interventionMode: rule.interventionMode ?? null,
      });
    }
  }
  return atoms;
}

function modesOverlapBetweenAtoms(
  left: ScopeFlatAtom,
  right: ScopeFlatAtom,
  headerMode: PartnerMaintenanceInterventionMode | null | undefined,
): boolean {
  return scopeInterventionModesOverlap(
    resolveEffectiveScopeInterventionMode(left.interventionMode, headerMode),
    resolveEffectiveScopeInterventionMode(right.interventionMode, headerMode),
  );
}

/** Same dimension + value cannot be both INCLUDE and EXCLUDE (overlapping intervention mode). */
export function findScopeIncludeExcludeConflicts(
  rules: ScopeRuleLike[],
  headerMode: PartnerMaintenanceInterventionMode | null | undefined,
): ScopeIncludeExcludeConflict[] {
  const atoms = flattenScopeEffectAtoms(rules);
  const seen = new Set<string>();
  const conflicts: ScopeIncludeExcludeConflict[] = [];

  for (let i = 0; i < atoms.length; i++) {
    for (let j = i + 1; j < atoms.length; j++) {
      const left = atoms[i];
      const right = atoms[j];
      if (left.dimension !== right.dimension || left.value !== right.value) {
        continue;
      }
      if (left.effect === right.effect) {
        continue;
      }
      if (!modesOverlapBetweenAtoms(left, right, headerMode)) {
        continue;
      }
      const includeAtom = left.effect === 'INCLUDE' ? left : right;
      const excludeAtom = left.effect === 'EXCLUDE' ? left : right;
      const key = `${includeAtom.dimension}:${includeAtom.value}:${includeAtom.ruleId}:${excludeAtom.ruleId}`;
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      conflicts.push({
        dimension: includeAtom.dimension,
        value: includeAtom.value,
        includeRuleId: includeAtom.ruleId,
        excludeRuleId: excludeAtom.ruleId,
      });
    }
  }

  return conflicts;
}

export function isScopeValueBlockedByOppositeEffectInDraft(args: {
  selfRuleId: string;
  selfEffect: PartnerMaintenanceScopeEffect;
  dimension: PartnerMaintenanceScopeDimension;
  value: string;
  ruleScopeMode: PartnerMaintenanceScopeInterventionMode | null | undefined;
  rules: ScopeRuleLike[];
  headerMode: PartnerMaintenanceInterventionMode | null | undefined;
}): boolean {
  const opposite: PartnerMaintenanceScopeEffect =
    args.selfEffect === 'INCLUDE' ? 'EXCLUDE' : 'INCLUDE';
  const selfAtom: ScopeFlatAtom = {
    ruleId: args.selfRuleId,
    dimension: args.dimension,
    effect: args.selfEffect,
    value: args.value,
    interventionMode: args.ruleScopeMode ?? null,
  };

  for (const atom of flattenScopeEffectAtoms(args.rules)) {
    if (atom.ruleId === args.selfRuleId) {
      continue;
    }
    if (atom.dimension !== args.dimension || atom.value !== args.value) {
      continue;
    }
    if (atom.effect !== opposite) {
      continue;
    }
    if (modesOverlapBetweenAtoms(selfAtom, atom, args.headerMode)) {
      return true;
    }
  }

  return false;
}
